import React, { useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { getDocument } from 'pdfjs-dist';
import * as UTIF from 'utif';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import SVG from "react-inlinesvg";
import '../_pdfWorker';

interface UploadPagesModalProps {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    /** 
     * Receives an array of base64 strings for each "page" 
     * (PDF pages or image files)
     */
    onPagesAdded: (pages: any[]) => void;
}

const UploadPagesModal: React.FC<UploadPagesModalProps> = ({
    show,
    setShow,
    onPagesAdded,
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [previews, setPreviews] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const unsupportedFormatMessage = "Only PDF, JPEG, PNG, TIFF formats are allowed.";

    const isPdfFile = (file: File) =>
        file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

    const isTiffFile = (file: File) =>
        file.type === "image/tiff" || /\.(tif|tiff)$/i.test(file.name);

    const isJpegFile = (file: File) =>
        file.type === "image/jpeg" || /\.(jpg|jpeg)$/i.test(file.name);

    const isPngFile = (file: File) =>
        file.type === "image/png" || file.name.toLowerCase().endsWith(".png");

    const handleClose = () => {
        if (!isProcessing) {
            setShow(false);
            setError(null);
            setPreviews([]);
        }
    };

    const handleFiles = async (files: FileList | null) => {
        if (!files) return;
        setError(null);
        setIsProcessing(true);
        const pages: string[] = [];
        let hasUnsupportedFormat = false;
        try {
            for (const file of Array.from(files)) {
                if (isPdfFile(file)) {
                    const data = await file.arrayBuffer();
                    const uint8 = new Uint8Array(data);
                    const loadingTask = getDocument({ data: uint8 });
                    const pdf = await loadingTask.promise;
                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page = await pdf.getPage(i);
                        const viewport = page.getViewport({ scale: 1.5 });
                        const canvas = document.createElement('canvas');
                        canvas.width = viewport.width;
                        canvas.height = viewport.height;
                        await page
                            .render({ canvasContext: canvas.getContext('2d')!, viewport })
                            .promise;
                        pages.push(canvas.toDataURL('image/png'));
                    }
                }
                else if (isTiffFile(file)) {
                    const arrayBuffer = await file.arrayBuffer();

                    const ifds = UTIF.decode(arrayBuffer);

                    for (const ifd of ifds) {
                        UTIF.decodeImage(arrayBuffer, ifd);
                        const rgba = UTIF.toRGBA8(ifd);

                        const canvas = document.createElement('canvas');
                        canvas.width = ifd.width;
                        canvas.height = ifd.height;
                        const ctx = canvas.getContext('2d')!;
                        const imgData = ctx.createImageData(ifd.width, ifd.height);
                        imgData.data.set(rgba);
                        ctx.putImageData(imgData, 0, 0);

                        pages.push(canvas.toDataURL('image/png'));
                    }
                }
                else if (isJpegFile(file) || isPngFile(file)) {
                    const dataUrl: string = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                    pages.push(dataUrl);
                }
                else {
                    hasUnsupportedFormat = true;
                }
            }

            setPreviews(pages);
            if (hasUnsupportedFormat) {
                setError(unsupportedFormatMessage);
            }
        } catch (e) {
            console.error(e);
            setError('Failed to process one or more files.');
        } finally {
            setIsProcessing(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDrop = (ev: React.DragEvent<HTMLDivElement>) => {
        ev.preventDefault();
        handleFiles(ev.dataTransfer.files);
    };
    const handleDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
        ev.preventDefault();
    };

    const handleAddClick = () => {
        if (previews.length === 0) {
            setError('Please select at least one page or image.');
            return;
        }

        const pages = previews.map((b64, i) => ({
            base64: b64,
            pageIndex: i,
            docId: 'upload',
            rotate: 0,
            isNewPage: true
        }));

        onPagesAdded(pages);
        handleClose();
    };

    return (
        <div>
            <Modal
                className='import-popup'
                show={show}
                onHide={handleClose}
                animation={false}
                size='lg'
                aria-labelledby='contained-modal-title-vcenter'
                centered
            >
                <Modal.Header
                    closeButton={!isProcessing}
                    className='d-block justify-content-start'
                >
                    <Modal.Title className='d-flex align-items-stretch justify-content-between'>
                        <div className=''>Upload Pages</div>
                        <i
                            title='Close'
                            className='fa-light fa-xmark d-flex justify-content-end align-items-center'
                            aria-hidden='true'
                            onClick={handleClose}
                        />
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {
                        error && (
                            <div className="alert alert-danger" >
                                {error}
                            </div>
                        )
                    }
                    <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        style={{
                            height: '250px',
                            border: '2px dashed #cccccc',
                            borderRadius: '5px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '20px',
                            position: 'relative',
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >

                        <span className='align-self-center svg-icon svg-icon-primary ml-3'>
                            <SVG
                                style={{ fill: "#247DBB", marginBottom: '8px' }}
                                src={toAbsoluteUrl('/media/svg/icons/Home/FilePdf.svg')}
                            />
                        </span>

                        {
                            isProcessing
                                ? <Spinner animation="border" />
                                :
                                <label htmlFor='fileInput'>
                                    Drag & Drop or Click to Upload PDF or images here
                                </label>
                        }


                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,image/jpeg,image/png,image/tiff,.tif,.tiff,.jpg,.jpeg,.png,.pdf"
                            multiple
                            style={{ display: 'none' }}
                            disabled={isProcessing}
                            onChange={e => handleFiles(e.target.files)}
                        />
                    </div>

                    {
                        previews.length > 0 && (
                            <div
                                style={
                                    {
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                        gap: 8,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                    }
                                }
                            >
                                {
                                    previews.map((src, i) => (
                                        <img
                                            key={i}
                                            src={src}
                                            alt={`page-${i + 1}`}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                border: '1px solid #ddd',
                                                borderRadius: 4,
                                            }
                                            }
                                        />
                                    ))}
                            </div>
                        )}
                </Modal.Body>

                <Modal.Footer className='d-block'>
                    <div className='d-flex footer-btn align-items-stretch justify-content-between'>
                        <div className='d-flex'>
                            <Button
                                title='Cancel'
                                variant='secondary'
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                        </div>
                        <div className='d-flex'>
                            <Button
                                title='Import'
                                variant='success'
                                onClick={handleAddClick}
                                disabled={isProcessing || previews.length === 0}
                            >
                                {
                                    isProcessing
                                        ?
                                        (<>Processing... <Spinner as="span" animation="border" size="sm" />
                                        </>)
                                        : 'Add Pages'
                                }
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>

    );
};

export default UploadPagesModal;
